import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import {
  RefreshCw,
  Clock,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  HelpCircle,
  FileText as ReportLogoIcon
} from 'lucide-react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TableSortLabel from '@mui/material/TableSortLabel';
import Collapse from '@mui/material/Collapse';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { styled } from '@mui/material/styles';
import api from '../services/api';
import { toast } from 'react-toastify';
import { useJob } from '../context/JobContext';
import { useAuth } from '../context/AuthContext';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StatCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2.5),
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)'
}));

export default function PraticheReportSintesi() {
  const { addJob } = useJob();
  const { user: authUser, loading: authLoading } = useAuth();
  const roleName = (authUser?.role_name || authUser?.role || '').toLowerCase();
  const isAdmin = roleName === 'admin';
  const initialLoadRef = useRef(false);
  const [referenceDate, setReferenceDate] = useState(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  });
  const [statusOptions, setStatusOptions] = useState([]);
  const [urgencyOptions, setUrgencyOptions] = useState([]);
  const [issuerOptions, setIssuerOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [legalOptions, setLegalOptions] = useState([]);
  const [comparePeriod, setComparePeriod] = useState('');
  const [workedPage, setWorkedPage] = useState(0);
  const [workedRowsPerPage, setWorkedRowsPerPage] = useState(10);
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(10);
  const [workedNotesPage, setWorkedNotesPage] = useState(0);
  const [workedNotesRowsPerPage, setWorkedNotesRowsPerPage] = useState(10);
  const [workedNotesOrderDirection, setWorkedNotesOrderDirection] = useState('desc');
  const [listOrderBy, setListOrderBy] = useState('severity');
  const [listOrderDirection, setListOrderDirection] = useState('desc');
  const [workedOrderBy, setWorkedOrderBy] = useState('severity');
  const [workedOrderDirection, setWorkedOrderDirection] = useState('desc');
  const [onlyLegalNotes, setOnlyLegalNotes] = useState(false);
  const [expandedNoteRow, setExpandedNoteRow] = useState(null);
  const [expandedNoteData, setExpandedNoteData] = useState({});
  const [expandedNoteLoading, setExpandedNoteLoading] = useState(false);
  const [summaryTab, setSummaryTab] = useState('buckets');
  const [filters, setFilters] = useState({
    status: '',
    issuer: '',
    category: '',
    urgency: '',
    rif_responsabile_legale: ''
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [counterpartySearch, setCounterpartySearch] = useState('');
  const [counterpartyPage, setCounterpartyPage] = useState(0);
  const [counterpartyRowsPerPage, setCounterpartyRowsPerPage] = useState(10);

  const fetchFilterOptions = async () => {
    try {
      const [catRes, issuerRes, filtersRes, usersRes] = await Promise.all([
        api.get('/category-negotiation'),
        api.get('/issuer'),
        api.get('/negotiation/filters'),
        isAdmin ? api.get('/lorenzo-users') : Promise.resolve({ data: [] })
      ]);
      setCategoryOptions(
        [...new Set((catRes.data || []).map((c) => c.name).filter(Boolean))].sort((a, b) =>
          a.localeCompare(b, 'it', { sensitivity: 'base' })
        )
      );
      setIssuerOptions(
        [...new Set((issuerRes.data || []).map((i) => i.name).filter(Boolean))].sort((a, b) =>
          a.localeCompare(b, 'it', { sensitivity: 'base' })
        )
      );
      const f = filtersRes.data || {};
      setStatusOptions((f.statuses || []).filter(Boolean));
      setUrgencyOptions((f.urgencies || []).filter(Boolean));

      if (isAdmin) {
        const users = usersRes.data || [];
        const sortedUsers = [...users].sort((a, b) => {
          const nameA = [a.name, a.surname].filter(Boolean).join(' ') || '';
          const nameB = [b.name, b.surname].filter(Boolean).join(' ') || '';
          return nameA.localeCompare(nameB, 'it', { sensitivity: 'base' });
        });
        const specialOption = {
          id: -1,
          name: 'Senza referente legale',
          surname: ''
        };
        setLegalOptions([specialOption, ...sortedUsers]);
        return;
      }

      const currentOption = {
        id: authUser?.id,
        name: authUser?.name || '',
        surname: authUser?.surname || ''
      };
      setLegalOptions([currentOption]);
      setFilters((prev) => ({
        ...prev,
        rif_responsabile_legale: authUser?.id != null ? String(authUser.id) : ''
      }));
    } catch (error) {
      console.error('Error fetching filter options for pratiche report:', error);
    }
  };

  const fetchReport = async (withNotification = false) => {
    let toastId = null;
    if (withNotification) {
      toastId = toast.info('Aggiornamento quadro di sintesi in corso...', {
        autoClose: false
      });
    }
    setLoading(true);
    try {
      const params = {
        status: filters.status || '',
        issuer: filters.issuer || '',
        category: filters.category || '',
        urgency: filters.urgency || '',
        reference_date: referenceDate || '',
        only_legal_notes: onlyLegalNotes ? 'true' : 'false'
      };
      if (filters.rif_responsabile_legale) {
        params.rif_responsabile_legale = filters.rif_responsabile_legale;
      }
      if (comparePeriod) {
        params.compare_period = comparePeriod;
      }
      const res = await api.get('/reports/pratiche-summary', { params });
      setData(res.data);
      if (withNotification && toastId) {
        toast.update(toastId, {
          render: 'Aggiornamento quadro di sintesi completato',
          type: 'success',
          autoClose: 3000
        });
      }
    } catch (error) {
      console.error('Error loading pratiche summary:', error);
      if (withNotification && toastId) {
        toast.update(toastId, {
          render: 'Errore durante l’aggiornamento del quadro di sintesi',
          type: 'error',
          autoClose: 5000
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authLoading) return;
    if (!authUser) return;
    fetchFilterOptions();
  }, [authLoading, authUser, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialLoadRef.current) return;
    if (authLoading) return;
    if (!authUser) return;
    if (!isAdmin && !filters.rif_responsabile_legale) return;
    fetchReport();
    initialLoadRef.current = true;
  }, [authLoading, authUser, isAdmin, filters.rif_responsabile_legale]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChangeFilter = (field) => (event) => {
    const value = event.target.value;
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleChangeReferenceDate = (event) => {
    const value = event.target.value;
    setReferenceDate(value);
  };

  const handleApply = () => {
    fetchReport(true);
  };

  const handleExportPdf = async () => {
    try {
      const res = await api.post('/reports/pratiche-summary', {
        filters,
        reference_date: referenceDate || ''
      });
      if (res.data && res.data.jobId) {
        addJob(res.data.jobId, 'pratiche_summary');
      }
    } catch (error) {
      console.error('Error starting pratiche summary PDF:', error);
    }
  };

  if (!data) {
    return (
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200
        }}
      >
        {loading ? (
          <RefreshCw className="animate-spin h-8 w-8 text-gray-400" />
        ) : (
          <Typography variant="h6" color="text.secondary">
            Nessun dato disponibile
          </Typography>
        )}
      </Box>
    );
  }

  const bucketLabels = [
    'Oggi (0 giorni)',
    'In regola (1-30 giorni)',
    'Attenzione (31-60 giorni)',
    'Critico (> 60 giorni)',
    'Senza note'
  ];

  const summaryByBucket = bucketLabels.map((label) => ({
    label,
    count: (data.groupedNegotiations && data.groupedNegotiations[label]
      ? data.groupedNegotiations[label].length
      : 0)
  }));

  const comparison = data.comparison || null;
  const comparisonLabelMap = {
    yesterday: 'vs ieri',
    '7d': 'vs 7 giorni fa',
    '30d': 'vs 30 giorni fa'
  };
  const comparisonLabel = comparison ? comparisonLabelMap[comparison.period] || '' : '';

  const comparisonBaseLabelMap = {
    yesterday: 'ieri',
    '7d': '7 giorni fa',
    '30d': '30 giorni fa'
  };
  const comparisonBaseLabel = comparison
    ? comparisonBaseLabelMap[comparison.period] || ''
    : '';

  const formatDateLabel = (iso) => {
    if (!iso) return '';
    const parts = String(iso).split('-');
    if (parts.length !== 3) return iso;
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const formatDateTimeWithWeekday = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString('it-IT', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getDaysDiffBetweenDates = (current, previous) => {
    if (!current || !previous) return null;
    const curr = new Date(current);
    const prev = new Date(previous);
    if (Number.isNaN(curr.getTime()) || Number.isNaN(prev.getTime())) return null;
    const msPerDay = 24 * 60 * 60 * 1000;
    const diff = Math.round((curr.getTime() - prev.getTime()) / msPerDay);
    return diff;
  };

  const getDaysDiffStatusColor = (days) => {
    if (days == null || Number.isNaN(days)) return '#6b7280';
    if (days <= 30) return '#2e7d32';
    if (days <= 60) return '#ed6c02';
    return '#d32f2f';
  };

  const handleToggleNotesRow = async (row) => {
    const rowId = row.id;
    if (!rowId) {
      return;
    }
    if (expandedNoteRow === rowId) {
      setExpandedNoteRow(null);
      return;
    }
    if (expandedNoteData[rowId]) {
      setExpandedNoteRow(rowId);
      return;
    }
    try {
      setExpandedNoteLoading(true);
      const res = await api.get(`/negotiation/${rowId}/notes`);
      setExpandedNoteData((prev) => ({
        ...prev,
        [rowId]: res.data || []
      }));
      setExpandedNoteRow(rowId);
    } catch (error) {
      console.error('Error fetching negotiation notes for report:', error);
    } finally {
      setExpandedNoteLoading(false);
    }
  };

  const stripHtml = (html) => {
    if (!html || typeof html !== 'string') return '';
    const div = document.createElement('div');
    div.innerHTML = html;
    return div.textContent || div.innerText || '';
  };

  const referenceDateLabel = comparison && comparison.referenceDate
    ? formatDateLabel(comparison.referenceDate)
    : formatDateLabel(referenceDate);

  const compareDateLabel = comparison && comparison.compareReferenceDate
    ? formatDateLabel(comparison.compareReferenceDate)
    : '';

  const formatDiff = (value) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '0';
    if (value > 0) return `+${value}`;
    return String(value);
  };

  const getDiffColor = (value) => {
    if (value > 0) return '#2e7d32';
    if (value < 0) return '#d32f2f';
    return '#6b7280';
  };

  const getBucketIconConfig = (label) => {
    if (label === 'Oggi (0 giorni)') {
      return { Icon: Clock, color: '#1976d2', bg: 'rgba(25, 118, 210, 0.08)' };
    }
    if (label === 'In regola (1-30 giorni)') {
      return { Icon: CheckCircle2, color: '#2e7d32', bg: 'rgba(46, 125, 50, 0.08)' };
    }
    if (label === 'Attenzione (31-60 giorni)') {
      return { Icon: AlertTriangle, color: '#ed6c02', bg: 'rgba(237, 108, 2, 0.08)' };
    }
    if (label === 'Critico (> 60 giorni)') {
      return { Icon: XCircle, color: '#d32f2f', bg: 'rgba(211, 47, 47, 0.08)' };
    }
    return { Icon: HelpCircle, color: '#6b7280', bg: 'rgba(107, 114, 128, 0.08)' };
  };

  const bucketFromDays = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return 'Senza note';
    }
    const d = Number(value);
    if (d === 0) return 'Oggi (0 giorni)';
    if (d >= 1 && d <= 30) return 'In regola (1-30 giorni)';
    if (d >= 31 && d <= 60) return 'Attenzione (31-60 giorni)';
    if (d > 60) return 'Critico (> 60 giorni)';
    return 'Senza note';
  };

  const getBucketSeverityScore = (label) => {
    if (label === 'Oggi (0 giorni)') return 1;
    if (label === 'In regola (1-30 giorni)') return 2;
    if (label === 'Attenzione (31-60 giorni)') return 3;
    if (label === 'Critico (> 60 giorni)') return 4;
    if (label === 'Senza note') return 5;
    return 3;
  };

  const getChangeDotColor = (previousLabel, currentLabel) => {
    const prevScore = getBucketSeverityScore(previousLabel);
    const currScore = getBucketSeverityScore(currentLabel);
    if (currScore > prevScore) {
      return '#d32f2f'; // peggioramento
    }
    if (currScore < prevScore) {
      return '#2e7d32'; // miglioramento
    }
    return '#6b7280'; // invariato / fallback
  };

  const totalNegotiations =
    typeof data.totalNegotiations === 'number'
      ? data.totalNegotiations
      : (data.negotiations || []).length;

  const allNegotiations = data.negotiations || [];

  const sortedNegotiations = [...allNegotiations].sort((a, b) => {
    if (listOrderBy === 'severity') {
      const sa = getBucketSeverityScore(bucketFromDays(a.giorni_dall_ultima_nota));
      const sb = getBucketSeverityScore(bucketFromDays(b.giorni_dall_ultima_nota));
      if (sa !== sb) {
        return listOrderDirection === 'asc' ? sa - sb : sb - sa;
      }
      const da = a.giorni_dall_ultima_nota || 0;
      const db = b.giorni_dall_ultima_nota || 0;
      return listOrderDirection === 'asc' ? da - db : db - da;
    }

    const getField = (item) => {
      switch (listOrderBy) {
        case 'cliente':
          return (item.customer_full_name || '').toLowerCase();
        case 'controparte':
          return (item.controparte || '').toLowerCase();
        case 'ref_legale':
          return (item.ref_legale || '').toLowerCase();
        case 'stato':
          return (item.status || '').toLowerCase();
        case 'urgenza':
          return (item.urgency || '').toLowerCase();
        default:
          return '';
      }
    };

    const fa = getField(a);
    const fb = getField(b);
    if (fa === fb) return 0;
    if (listOrderDirection === 'asc') {
      return fa < fb ? -1 : 1;
    }
    return fa > fb ? -1 : 1;
  });

  const paginatedNegotiations = sortedNegotiations.slice(
    listPage * listRowsPerPage,
    listPage * listRowsPerPage + listRowsPerPage
  );

  const counterpartyStatsArray = Object.keys(data.counterpartyStats || {}).map((cp) => ({
    counterparty: cp,
    stats: data.counterpartyStats[cp]
  }));

  const filteredCounterpartyStatsArray = counterpartyStatsArray.filter(({ counterparty }) => {
    if (!counterpartySearch) return true;
    return counterparty.toLowerCase().includes(counterpartySearch.toLowerCase());
  });

  const paginatedCounterpartyStatsArray = filteredCounterpartyStatsArray.slice(
    counterpartyPage * counterpartyRowsPerPage,
    counterpartyPage * counterpartyRowsPerPage + counterpartyRowsPerPage
  );

  const workedItems = (data.comparison && data.comparison.worked) || [];
  const workedNotesItems =
    (data.comparison && data.comparison.workedWithNotes) || [];

  const sortedWorkedItems = [...workedItems].sort((a, b) => {
    if (workedOrderBy === 'severity') {
      const sa = getBucketSeverityScore(a.bucket_current || '');
      const sb = getBucketSeverityScore(b.bucket_current || '');
      if (sa !== sb) {
        return workedOrderDirection === 'asc' ? sa - sb : sb - sa;
      }
      const da = a.giorni_current || 0;
      const db = b.giorni_current || 0;
      return workedOrderDirection === 'asc' ? da - db : db - da;
    }

    if (workedOrderBy === 'cliente') {
      const fa = (a.customer_full_name || '').toLowerCase();
      const fb = (b.customer_full_name || '').toLowerCase();
      if (fa === fb) return 0;
      if (workedOrderDirection === 'asc') {
        return fa < fb ? -1 : 1;
      }
      return fa > fb ? -1 : 1;
    }

    return 0;
  });

  const paginatedWorkedItems = sortedWorkedItems.slice(
    workedPage * workedRowsPerPage,
    workedPage * workedRowsPerPage + workedRowsPerPage
  );

  const sortedWorkedNotesItems = [...workedNotesItems].sort((a, b) => {
    const da = getDaysDiffBetweenDates(a.last_note_current, a.last_note_previous) || 0;
    const db = getDaysDiffBetweenDates(b.last_note_current, b.last_note_previous) || 0;
    return workedNotesOrderDirection === 'asc' ? da - db : db - da;
  });

  const paginatedWorkedNotesItems = sortedWorkedNotesItems.slice(
    workedNotesPage * workedNotesRowsPerPage,
    workedNotesPage * workedNotesRowsPerPage + workedNotesRowsPerPage
  );

  const handleCounterpartySearchChange = (event) => {
    setCounterpartySearch(event.target.value || '');
    setCounterpartyPage(0);
  };

  const handleChangeCounterpartyPage = (event, newPage) => {
    setCounterpartyPage(newPage);
  };

  const handleChangeCounterpartyRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10) || 10;
    setCounterpartyRowsPerPage(value);
    setCounterpartyPage(0);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ReportLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
              Report Pratiche
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Elaborato da: {data.userName} il {data.generatedAt}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={handleExportPdf}>
            Report di Sintesi
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={async () => {
              try {
                const res = await api.post('/reports/pratiche-legal', {
                  filters,
                  reference_date: referenceDate || ''
                });
                if (res.data && res.data.jobId) {
                  addJob(res.data.jobId, 'pratiche_legal');
                }
              } catch (error) {
                console.error('Error starting pratiche legal report:', error);
              }
            }}
          >
            Report Legale
          </Button>
        </Box>
      </Box>

      <StyledCard sx={{ mb: 3, p: 2.5 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={statusOptions}
              value={filters.status || ''}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, status: newValue || '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Stato pratica"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={legalOptions}
              getOptionLabel={(option) => {
                if (!option) return '';
                const name = [option.name, option.surname].filter(Boolean).join(' ');
                return name || `ID ${option.id}`;
              }}
              value={
                legalOptions.find((u) => String(u.id) === String(filters.rif_responsabile_legale)) || null
              }
              onChange={(event, newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  rif_responsabile_legale: newValue && newValue.id ? String(newValue.id) : ''
                }));
              }}
              disabled={!isAdmin}
              disableClearable={!isAdmin}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Referente legale"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={issuerOptions}
              value={filters.issuer || ''}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, issuer: newValue || '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Controparte"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              fullWidth
              size="small"
              options={categoryOptions}
              value={filters.category || ''}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, category: newValue || '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Categoria"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Autocomplete
              fullWidth
              size="small"
              options={urgencyOptions}
              value={filters.urgency || ''}
              onChange={(event, newValue) => {
                setFilters((prev) => ({ ...prev, urgency: newValue || '' }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Urgenza"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="Al"
              value={referenceDate}
              onChange={handleChangeReferenceDate}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="confronto-label">Confronto</InputLabel>
              <Select
                labelId="confronto-label"
                label="Confronto"
                value={comparePeriod}
                onChange={(event) => setComparePeriod(event.target.value)}
              >
                <MenuItem value="">Nessuno</MenuItem>
                <MenuItem value="yesterday">1 giorno rispetto alla data "Al"</MenuItem>
                <MenuItem value="7d">7 giorni rispetto alla data "Al"</MenuItem>
                <MenuItem value="30d">30 giorni rispetto alla data "Al"</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={onlyLegalNotes}
                  onChange={(event) => setOnlyLegalNotes(event.target.checked)}
                />
              }
              label="Considera solo note referente legale"
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleApply}
              disabled={loading}
              startIcon={<RefreshCw size={16} />}
            >
              Applica
            </Button>
          </Grid>
        </Grid>
      </StyledCard>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {summaryByBucket.map((item) => {
          const { Icon, color, bg } = getBucketIconConfig(item.label);
          const diff =
            comparison && comparison.buckets && comparison.buckets[item.label]
              ? comparison.buckets[item.label].diff || 0
              : 0;
          return (
            <Grid item xs={12} md={2} key={item.label}>
              <StatCard>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 0.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="subtitle2">{item.label}</Typography>
                    <Tooltip
                      title={
                        compareDateLabel
                          ? `Valore = N. pratiche al ${referenceDateLabel}. Diff = N. pratiche al ${referenceDateLabel} − N. pratiche al ${compareDateLabel}. Diff > 0: più pratiche rispetto alla data di confronto. Diff < 0: meno pratiche. Diff = 0: nessuna variazione.`
                          : `Valore = N. pratiche al giorno indicato nel campo "Al".`
                      }
                      arrow
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 14, color: 'text.disabled', cursor: 'default' }}
                      />
                    </Tooltip>
                  </Box>
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '999px',
                      backgroundColor: bg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <Icon size={18} color={color} />
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                  {item.count}
                </Typography>
                {comparison && comparisonLabel && (
                  <Typography
                    variant="caption"
                    sx={{ mt: 0.5, color: getDiffColor(diff) }}
                  >
                    {comparisonLabel}: {formatDiff(diff)}
                  </Typography>
                )}
              </StatCard>
            </Grid>
          );
        })}
        <Grid item xs={12} md={2}>
          <StatCard>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Totale
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {totalNegotiations}
            </Typography>
            {comparison && comparison.total && comparisonLabel && (
              <>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.5, color: getDiffColor(comparison.total.diff || 0) }}
                >
                  {comparisonLabel}: {formatDiff(comparison.total.diff || 0)}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.25, display: 'block', color: 'text.secondary' }}
                >
                  {compareDateLabel
                    ? `Confronto tra ${referenceDateLabel} e ${compareDateLabel}`
                    : ''}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ mt: 0.25, display: 'block', color: 'text.secondary' }}
                >
                  {compareDateLabel
                    ? `Diff = N. pratiche al ${referenceDateLabel} − N. pratiche al ${compareDateLabel}`
                    : ''}
                </Typography>
              </>
            )}
          </StatCard>
        </Grid>
      </Grid>

      <StyledCard sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5
            }}
          >
            <Typography variant="h6">
              Quadro di Sintesi
            </Typography>
            {(
              comparison ||
              (data.negotiations && data.negotiations.length > 0)
            ) && (
              <Tabs
                value={summaryTab}
                onChange={(event, newValue) => setSummaryTab(newValue)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab value="buckets" label="Riepilogo" />
                {data.negotiations && data.negotiations.length > 0 && (
                  <Tab
                    value="list"
                    label={`Pratiche (${data.negotiations.length})`}
                  />
                )}
                {comparison && (
                  <Tab
                    value="worked"
                    label={`Pratiche con cambio fascia (${workedItems.length})`}
                  />
                )}
                {comparison && (
                  <Tab
                    value="worked_notes"
                    label={`Pratiche con nuove note (${workedNotesItems.length})`}
                  />
                )}
              </Tabs>
            )}
          </Box>

          {summaryTab === 'worked' && comparison && workedItems.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Sono elencate le pratiche che hanno cambiato fascia di giorni dall&apos;ultima
                nota tra {referenceDateLabel} e {compareDateLabel}.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rif. pratica</TableCell>
                      <TableCell sortDirection={workedOrderBy === 'cliente' ? workedOrderDirection : false}>
                        <TableSortLabel
                          active={workedOrderBy === 'cliente'}
                          direction={workedOrderDirection}
                          onClick={() => {
                            setWorkedOrderBy('cliente');
                            setWorkedOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Cliente
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Controparte</TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Urgenza</TableCell>
                      <TableCell>Data assegnazione legale</TableCell>
                      <TableCell>Referente legale</TableCell>
                      <TableCell sortDirection={workedOrderBy === 'severity' ? workedOrderDirection : false}>
                        <TableSortLabel
                          active={workedOrderBy === 'severity'}
                          direction={workedOrderDirection}
                          onClick={() => {
                            setWorkedOrderBy('severity');
                            setWorkedOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Fascia attuale
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Fascia precedente</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedWorkedItems.map((row) => {
                      const dotColor = getChangeDotColor(
                        row.bucket_previous,
                        row.bucket_current
                      );
                      return (
                        <TableRow key={row.id}>
                          <TableCell>{row.pratica_reference || row.id}</TableCell>
                          <TableCell>{row.customer_full_name || '-'}</TableCell>
                          <TableCell>{row.controparte || '-'}</TableCell>
                          <TableCell>{row.category_name || '-'}</TableCell>
                          <TableCell>{row.status || '-'}</TableCell>
                          <TableCell>{row.urgency || '-'}</TableCell>
                          <TableCell>
                            {row.data_assegnazione_legale
                              ? new Date(row.data_assegnazione_legale).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell>{row.ref_legale || '-'}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '999px',
                                  backgroundColor: dotColor
                                }}
                              />
                              <span>{row.bucket_current || '-'}</span>
                            </Box>
                          </TableCell>
                          <TableCell>{row.bucket_previous || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={workedItems.length}
                page={workedPage}
                onPageChange={(event, newPage) => setWorkedPage(newPage)}
                rowsPerPage={workedRowsPerPage}
                onRowsPerPageChange={(event) => {
                  const value = parseInt(event.target.value, 10) || 10;
                  setWorkedRowsPerPage(value);
                  setWorkedPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          ) : summaryTab === 'worked_notes' &&
            comparison &&
            workedNotesItems.length > 0 ? (
            <>
              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Sono elencate le pratiche per cui la data dell&apos;ultima nota è cambiata tra{' '}
                {referenceDateLabel} e {compareDateLabel}.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Rif. pratica</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Controparte</TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell>Stato</TableCell>
                      <TableCell>Urgenza</TableCell>
                      <TableCell>Data assegnazione legale</TableCell>
                      <TableCell>Referente legale</TableCell>
                      <TableCell>Fascia attuale</TableCell>
                      <TableCell>Fascia precedente</TableCell>
                      <TableCell>Ultima nota attuale</TableCell>
                      <TableCell>Ultima nota precedente</TableCell>
                      <TableCell sortDirection={workedNotesOrderDirection}>
                        <TableSortLabel
                          active
                          direction={workedNotesOrderDirection}
                          onClick={() =>
                            setWorkedNotesOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            )
                          }
                        >
                          Aggiornata dopo giorni
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedWorkedNotesItems.map((row) => {
                      const diffDays = getDaysDiffBetweenDates(
                        row.last_note_current,
                        row.last_note_previous
                      );
                      const dotColor = getDaysDiffStatusColor(diffDays);
                      return (
                        <React.Fragment key={row.id}>
                          <TableRow
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleToggleNotesRow(row)}
                          >
                            <TableCell padding="checkbox">
                              <IconButton
                                size="small"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleToggleNotesRow(row);
                                }}
                              >
                                {expandedNoteRow === row.id ? (
                                  <KeyboardArrowUpIcon fontSize="small" />
                                ) : (
                                  <KeyboardArrowDownIcon fontSize="small" />
                                )}
                              </IconButton>
                            </TableCell>
                            <TableCell>{row.pratica_reference || row.id}</TableCell>
                            <TableCell>{row.customer_full_name || '-'}</TableCell>
                            <TableCell>{row.controparte || '-'}</TableCell>
                            <TableCell>{row.category_name || '-'}</TableCell>
                            <TableCell>{row.status || '-'}</TableCell>
                            <TableCell>{row.urgency || '-'}</TableCell>
                            <TableCell>
                              {row.data_assegnazione_legale
                                ? new Date(row.data_assegnazione_legale).toLocaleDateString()
                                : '-'}
                            </TableCell>
                            <TableCell>{row.ref_legale || '-'}</TableCell>
                            <TableCell>{row.bucket_current || '-'}</TableCell>
                            <TableCell>{row.bucket_previous || '-'}</TableCell>
                            <TableCell>
                              {formatDateTimeWithWeekday(row.last_note_current)}
                            </TableCell>
                            <TableCell>
                              {formatDateTimeWithWeekday(row.last_note_previous)}
                            </TableCell>
                            <TableCell align="right">
                              {diffDays == null ? (
                                '-'
                              ) : (
                                <Box
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 10,
                                      height: 10,
                                      borderRadius: '999px',
                                      backgroundColor: dotColor
                                    }}
                                  />
                                  <span>{diffDays}</span>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={13}
                              sx={{ paddingBottom: 0, paddingTop: 0 }}
                            >
                              <Collapse
                                in={expandedNoteRow === row.id}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Box sx={{ margin: 1 }}>
                                  {expandedNoteLoading &&
                                  expandedNoteRow === row.id ? (
                                    <Typography
                                      variant="body2"
                                      sx={{ color: 'text.secondary' }}
                                    >
                                      Caricamento note...
                                    </Typography>
                                  ) : (
                                    <>
                                      {(() => {
                                        const allNotes = expandedNoteData[row.id] || [];
                                        const filteredNotes =
                                          onlyLegalNotes && row.legal_id
                                            ? allNotes.filter(
                                                (n) =>
                                                  n.rif_utente &&
                                                  Number(n.rif_utente) ===
                                                    Number(row.legal_id)
                                              )
                                            : allNotes;
                                        if (!filteredNotes.length) {
                                          return (
                                            <Typography
                                              variant="body2"
                                              sx={{ color: 'text.secondary' }}
                                            >
                                              Nessuna nota trovata.
                                            </Typography>
                                          );
                                        }
                                        return (
                                          <Table size="small">
                                            <TableHead>
                                              <TableRow>
                                                <TableCell>Data</TableCell>
                                                <TableCell>Autore</TableCell>
                                                <TableCell>Testo</TableCell>
                                              </TableRow>
                                            </TableHead>
                                            <TableBody>
                                              {filteredNotes.map((n) => (
                                                <TableRow key={n.id}>
                                                  <TableCell>
                                                    {formatDateTimeWithWeekday(
                                                      n.created_at
                                                    )}
                                                  </TableCell>
                                                  <TableCell>
                                                    {[n.user_name, n.user_surname]
                                                      .filter(Boolean)
                                                      .join(' ') ||
                                                      (n.rif_utente
                                                        ? `ID: ${n.rif_utente}`
                                                        : '-')}
                                                  </TableCell>
                                                  <TableCell>
                                                    {stripHtml(n.descrizione) || '-'}
                                                  </TableCell>
                                                </TableRow>
                                              ))}
                                            </TableBody>
                                          </Table>
                                        );
                                      })()}
                                    </>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={workedNotesItems.length}
                page={workedNotesPage}
                onPageChange={(event, newPage) => setWorkedNotesPage(newPage)}
                rowsPerPage={workedNotesRowsPerPage}
                onRowsPerPageChange={(event) => {
                  const value = parseInt(event.target.value, 10) || 10;
                  setWorkedNotesRowsPerPage(value);
                  setWorkedNotesPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          ) : summaryTab === 'list' && data.negotiations && data.negotiations.length > 0 ? (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Rif. pratica</TableCell>
                      <TableCell sortDirection={listOrderBy === 'cliente' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'cliente'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('cliente');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Cliente
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={listOrderBy === 'controparte' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'controparte'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('controparte');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Controparte
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Tipologia</TableCell>
                      <TableCell sortDirection={listOrderBy === 'stato' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'stato'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('stato');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Stato
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={listOrderBy === 'urgenza' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'urgenza'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('urgenza');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Urgenza
                        </TableSortLabel>
                      </TableCell>
                      <TableCell>Data assegnazione legale</TableCell>
                      <TableCell sortDirection={listOrderBy === 'ref_legale' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'ref_legale'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('ref_legale');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Referente legale
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={listOrderBy === 'severity' ? listOrderDirection : false}>
                        <TableSortLabel
                          active={listOrderBy === 'severity'}
                          direction={listOrderDirection}
                          onClick={() => {
                            setListOrderBy('severity');
                            setListOrderDirection((prev) =>
                              prev === 'asc' ? 'desc' : 'asc'
                            );
                          }}
                        >
                          Fascia attuale
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedNegotiations.map((n) => (
                      <TableRow key={n.id}>
                        <TableCell>{n.pratica_reference || n.id}</TableCell>
                        <TableCell>{n.customer_full_name || '-'}</TableCell>
                        <TableCell>{n.controparte || '-'}</TableCell>
                        <TableCell>{n.category_name || '-'}</TableCell>
                        <TableCell>{n.status || '-'}</TableCell>
                        <TableCell>{n.urgency || '-'}</TableCell>
                        <TableCell>
                          {n.data_assegnazione_legale
                            ? new Date(n.data_assegnazione_legale).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>{n.ref_legale || '-'}</TableCell>
                        <TableCell>{bucketFromDays(n.giorni_dall_ultima_nota)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={allNegotiations.length}
                page={listPage}
                onPageChange={(event, newPage) => setListPage(newPage)}
                rowsPerPage={listRowsPerPage}
                onRowsPerPageChange={(event) => {
                  const value = parseInt(event.target.value, 10) || 10;
                  setListRowsPerPage(value);
                  setListPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Stato Nota</TableCell>
                    <TableCell align="right">N. Pratiche</TableCell>
                    {comparison && comparisonBaseLabel && (
                      <>
                        <TableCell align="right">{`N. ${comparisonBaseLabel}`}</TableCell>
                        <TableCell align="right">Diff</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summaryByBucket.map((row) => {
                    const compBucket =
                      comparison && comparison.buckets && comparison.buckets[row.label]
                        ? comparison.buckets[row.label]
                        : null;
                    const previous = compBucket ? compBucket.previous || 0 : 0;
                    const diff = compBucket ? compBucket.diff || 0 : 0;
                    return (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{row.count}</TableCell>
                        {comparison && comparisonBaseLabel && (
                          <>
                            <TableCell align="right">{previous}</TableCell>
                            <TableCell
                              align="right"
                              sx={{ color: getDiffColor(diff) }}
                            >
                              {formatDiff(diff)}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                  <TableRow selected>
                    <TableCell>TOTALE</TableCell>
                    <TableCell align="right">{totalNegotiations}</TableCell>
                    {comparison && comparison.total && comparisonBaseLabel && (
                      <>
                        <TableCell align="right">
                          {comparison.total.previous || 0}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ color: getDiffColor(comparison.total.diff || 0) }}
                        >
                          {formatDiff(comparison.total.diff || 0)}
                        </TableCell>
                      </>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </StyledCard>

      <StyledCard sx={{ mb: 3 }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Riepilogo per Controparte
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
            <TextField
              size="small"
              label="Cerca controparte"
              value={counterpartySearch}
              onChange={handleCounterpartySearchChange}
            />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Controparte</TableCell>
                  <TableCell align="center">Oggi</TableCell>
                  <TableCell align="center">1-30</TableCell>
                  <TableCell align="center">31-60</TableCell>
                  <TableCell align="center">&gt;60</TableCell>
                  <TableCell align="center">No Note</TableCell>
                  <TableCell align="right">Tot</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedCounterpartyStatsArray.map(({ counterparty, stats }) => (
                  <TableRow key={counterparty}>
                    <TableCell>{counterparty}</TableCell>
                    <TableCell align="center">{stats['Oggi (0 giorni)'] || 0}</TableCell>
                    <TableCell align="center">{stats['In regola (1-30 giorni)'] || 0}</TableCell>
                    <TableCell align="center">{stats['Attenzione (31-60 giorni)'] || 0}</TableCell>
                    <TableCell align="center">{stats['Critico (> 60 giorni)'] || 0}</TableCell>
                    <TableCell align="center">{stats['Senza note'] || 0}</TableCell>
                    <TableCell align="right">{stats.Totale || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filteredCounterpartyStatsArray.length}
            page={counterpartyPage}
            onPageChange={handleChangeCounterpartyPage}
            rowsPerPage={counterpartyRowsPerPage}
            onRowsPerPageChange={handleChangeCounterpartyRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>
      </StyledCard>
    </Box>
  );
}
